// The parts an invoice is shown with (client-app/shared.jsx): the checks compact and in full, the five-step
// progress, where to fix, the PDF, PO vs invoice, the audit list and the technical details.
import { useEffect, useState, type ReactNode } from "react";
import { apiBlob, ApiError } from "../api/client";
import { useSyncore } from "../api/hooks";
import type { AuditEntry, InvoiceDetail, Line, Totals } from "../api/types";
import { useSession } from "../auth/SessionProvider";
import { CHECK_DESCRIPTIONS, countChecks, fixOf, plainSummary, plainSyncoreError, sortChecks, type CheckView, failedWording } from "./checks";
import { CheckIcon, Hint, KV, Spinner, mono, overline, th, td, table, type CheckResult } from "./components";
import { actorName, amount, eventWords, fmtDateTime, fmtWhen, invoiceRef, money, outcomeOf, signed, STEPS, stepWords } from "./format";
import { Button, Icon } from "../ui";

/** Who marked an invoice not approved: the name the API resolved, never the actor id. */
export function resolvedBy(detail: InvoiceDetail): string {
  if (detail.invoice.resolved) return actorName(detail.invoice.resolved.by);
  const entry = detail.audit.slice().reverse().find((e) => e.event === "InvoiceDismissed");
  return entry ? actorName(entry.actor) : "a former user";
}

export function ChecksCompact({ checks, failOnly, names, stack }: { checks: CheckView[]; failOnly?: boolean; names?: boolean; stack?: boolean }) {
  const named = sortChecks(checks.filter((c) => c.r === "no" || (!failOnly && c.r === "adj")));
  const ok = countChecks(checks, "ok") + (failOnly ? countChecks(checks, "adj") : 0);
  const na = countChecks(checks, "na");
  return (
    <span style={{ display: stack ? "flex" : "inline-flex", flexDirection: stack ? "column" : "row", flexWrap: stack ? "nowrap" : "wrap", alignItems: stack ? "flex-start" : "center", gap: stack ? "7px" : "5px 12px", font: "600 13px/1.3 var(--font-body)" }}>
      {named.map((c) => {
        const tip = names && c.d;
        const el = <span key={c.n} style={{ display: "inline-flex", alignItems: "center", gap: 6, whiteSpace: "nowrap", color: c.r === "no" ? "var(--status-mismatch-fg)" : "var(--status-review-fg)", cursor: tip ? "help" : undefined }}><CheckIcon r={c.r} size={16} /><span style={tip ? { borderBottom: "1px dotted currentColor" } : undefined}>{names ? (c.r === "no" ? failedWording(c.n) : c.n) : c.s || c.n}</span></span>;
        return tip ? <Hint key={c.n} text={c.d} width={260} below>{el}</Hint> : el;
      })}
      {ok > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--status-matched-fg)", fontWeight: 800 }}><CheckIcon r="ok" size={16} />{ok}{stack ? " passed" : ""}</span>}
      {na > 0 && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--text-subtle)" }}><CheckIcon r="na" size={16} />{na}{stack ? " not checked" : ""}</span>}
    </span>
  );
}

interface Figures { pre: string; inv: string; po: string; diff: string | null; allowed: string | null; note: string }

/** "Freight 64.68 vs PO 36.85 (+27.83) – allowed ±3.69" → its figures. */
export function figuresOf(d: string): Figures | null {
  if (!d) return null;
  const m = /^(.*?)\s*([−+-]?[\d,]+(?:\.\d+)?) vs PO ([−+-]?[\d,]+(?:\.\d+)?)(?: \(([^)]+)\))?(?: – (.+))?$/.exec(d);
  if (!m) return null;
  const t = m[5] || "";
  return { pre: m[1], inv: m[2], po: m[3], diff: m[4] ?? null, allowed: t.startsWith("allowed ") ? t.slice(8) : null, note: t.startsWith("allowed ") ? "" : t };
}

export function CheckDetail({ c }: { c: CheckView }) {
  const f = figuresOf(c.d);
  const tone = c.r === "no" ? "var(--status-mismatch-fg)" : c.r === "adj" ? "var(--status-review-fg)" : "var(--text-muted)";
  if (!f) return <div style={{ font: "600 13px/1.45 var(--font-body)", color: tone, marginTop: 3 }}>{c.d}</div>;
  const cells: [string, string | null][] = [["Invoice", f.inv], ["PO", f.po], ["Difference", f.diff], ["Allowed", f.allowed]];
  return (
    <div style={{ marginTop: 6, display: "flex", flexDirection: "column", gap: 4 }}>
      {f.pre && <span style={{ font: "600 12px var(--font-body)", color: "var(--text-muted)" }}>{f.pre}</span>}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,92px)", columnGap: 16 }}>
        {cells.map(([l, v]) => <div key={l} style={{ display: "flex", flexDirection: "column", gap: 3 }}><span style={{ font: "700 10.5px/1 var(--font-body)", letterSpacing: ".07em", textTransform: "uppercase", color: "var(--text-subtle)" }}>{l}</span><span style={{ font: "600 14px/1.3 var(--font-body)", fontVariantNumeric: "tabular-nums", color: l === "Difference" ? tone : v ? "var(--text-strong)" : "var(--text-subtle)" }}>{v || "—"}</span></div>)}
      </div>
      {f.note && <span style={{ font: "600 12px var(--font-body)", color: tone }}>{f.note}</span>}
    </div>
  );
}

export function ChecksList({ checks, cols = 2, only }: { checks: CheckView[]; cols?: number; only?: CheckResult[] }) {
  const list = sortChecks(only ? checks.filter((c) => only.includes(c.r)) : checks);
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols},minmax(0,1fr))`, gap: "12px 24px" }}>
      {list.map((c) => (
        <div key={c.n} style={{ display: "flex", gap: 10, alignItems: c.d ? "flex-start" : "center", gridColumn: figuresOf(c.d) ? "1 / -1" : undefined }}>
          <CheckIcon r={c.r} />
          <div style={{ minWidth: 0 }}>
            <div style={{ font: "700 14px/1.3 var(--font-body)", color: c.r === "no" ? "var(--status-mismatch-fg)" : c.r === "na" ? "var(--text-subtle)" : "var(--text-strong)" }}>
              <Hint text={CHECK_DESCRIPTIONS[c.n as keyof typeof CHECK_DESCRIPTIONS] ?? c.n} width={220}><span>{c.r === "no" ? failedWording(c.n) : c.n}</span></Hint>
            </div>
            {c.d && <CheckDetail c={c} />}
          </div>
        </div>
      ))}
    </div>
  );
}

export function ProgressTracker({ detail, checks, readyLine }: { detail: InvoiceDetail; checks: CheckView[]; readyLine: string }) {
  const inv = detail.invoice;
  const o = outcomeOf(inv);
  const fails = countChecks(checks, "no"), adj = countChecks(checks, "adj"), ok = countChecks(checks, "ok");
  const stop = o === "stop";
  const received = detail.timeline.find((t) => t.step === "Received");
  const read = detail.timeline.find((t) => t.step === "Extracted");
  const approvedAt = inv.approvedAt ?? detail.timeline.find((t) => t.step === "PurchaseOrderApproved")?.at ?? null;
  const tech = detail.outcome && stop && !inv.jobNumber ? plainSummary(inv) : null;
  const OUTS: Record<string, [string, string, string, string, string]> = {
    rev: ["var(--av-sun-400)", "Needs you", detail.attachment?.id ? "Attached with a note – PO left open" : "Nothing changed in Syncore", "var(--status-review-fg)", "flag"],
    stop: ["var(--av-sun-400)", "Needs you", "Nothing changed in Syncore", "var(--status-review-fg)", "octagon-x"],
    ready: ["var(--av-blue-500)", "Ready to approve", readyLine, "var(--text-brand)", "calendar-clock"],
    ok: ["var(--av-mint-500)", "Approved", inv.dryRun ? "Preview: nothing changed in Syncore" : (inv.code === "VOUCHED_IN_SYNCORE" ? "PO " + (inv.summary ?? "approved in Syncore").replace(/^Approved/, "approved") : "PO approved in Syncore") + (approvedAt ? "\n" + fmtWhen(approvedAt) : ""), "var(--status-matched-fg)", "check"],
    set: ["var(--av-blue-500)", "Waiting on settings", "Checked again once the setting is saved", "var(--text-brand)", "settings"],
    res: ["var(--av-gray-400)", "Not approved", "By " + resolvedBy(detail), "var(--text-muted)", "archive"],
    wait: ["var(--av-gray-400)", "Queued", "Waiting to be read", "var(--text-muted)", "clock"],
    prog: ["var(--av-blue-500)", "Comparing", stepWords(inv.step), "var(--text-brand)", "loader-circle"],
  };
  const out = OUTS[o] ?? OUTS.rev;
  const poFound = !!inv.jobNumber && !!inv.poNumber;
  const s: [string, string, string][] = [
    ["d", "Received", (inv.source === "email" ? "Email" : inv.source === "upload" ? "Upload" : "Received") + " – " + fmtWhen(received?.at ?? inv.receivedAt)],
    [read ? "d" : o === "wait" || o === "prog" ? "n" : "x", "Read", read?.durationMs ? (read.durationMs / 1000).toFixed(1) + " s" : read?.detail ?? (o === "wait" ? "Not yet" : "—")],
    [poFound ? "d" : stop || o === "rev" ? "x" : "n", "PO found", poFound ? `Job ${inv.jobNumber} – PO ${inv.poNumber}` : tech ? tech : detail.printedPo ? "Printed " + detail.printedPo : "No PO number"],
    [stop && !poFound ? "n" : fails ? "x" : o === "wait" || o === "prog" ? "n" : "d", "Checked", stop && !poFound ? "Not reached" : fails ? `${fails} of ${checks.length} failed` : o === "wait" || o === "prog" ? "Not yet" : `${ok} passed` + (adj ? ` – ${adj} adjusted` : "")],
    ["o", out[1], out[2]],
  ];
  return (
    <ol style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gridTemplateColumns: "repeat(5,minmax(0,1fr))" }}>
      {s.map((x, i) => {
        const bg = x[0] === "d" ? "var(--av-mint-500)" : x[0] === "x" ? "var(--av-coral-500)" : x[0] === "o" ? out[0] : "#fff";
        const ic = x[0] === "d" ? "check" : x[0] === "x" ? "x" : x[0] === "o" ? out[4] : null;
        return (
          <li key={i} style={{ position: "relative", paddingTop: 36, paddingRight: 12 }}>
            {i < 4 && <span style={{ position: "absolute", top: 12, left: 30, right: 6, height: 3, borderRadius: 2, background: x[0] === "d" ? "var(--av-mint-500)" : "var(--av-gray-100)" }} />}
            <span style={{ position: "absolute", top: 0, left: 0, width: 27, height: 27, borderRadius: "50%", background: bg, boxShadow: x[0] === "n" ? "inset 0 0 0 2px var(--av-gray-200)" : "none", color: x[0] === "o" && o === "rev" ? "var(--av-navy-900)" : "#fff", display: "flex", alignItems: "center", justifyContent: "center" }}>{ic && <Icon name={ic} size={14} stroke={3} />}</span>
            <div style={{ font: "800 14px/1.2 var(--font-display)", color: x[0] === "x" ? "var(--status-mismatch-fg)" : x[0] === "o" ? out[3] : x[0] === "n" ? "var(--text-subtle)" : "var(--text-strong)" }}>{x[1]}</div>
            <div style={{ font: "12.5px/1.35 var(--font-body)", color: "var(--text-muted)", marginTop: 3, whiteSpace: "pre-line" }}>{x[2]}</div>
          </li>
        );
      })}
    </ol>
  );
}

export function FixRow({ detail, onFix, syncoreUrl }: { detail: InvoiceDetail; onFix?: () => void; syncoreUrl?: string | null }) {
  const session = useSession();
  const f = fixOf(detail);
  if (!f) return null;
  const inv = detail.invoice;
  const hand = f.kind === "Manual";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", padding: "12px 14px 12px 18px", borderRadius: "var(--radius-lg)", background: "var(--status-review-bg)", boxShadow: "inset 0 0 0 1px rgba(255,191,71,.7)" }}>
      <span style={{ ...overline, color: "var(--status-review-fg)" }}>Fix in Syncore</span>
      {f.po && <span style={{ ...mono, fontSize: 14, color: "var(--text-strong)" }}>PO {f.po}</span>}
      <span style={{ font: "600 14px var(--font-body)", color: "var(--text-muted)" }}>{f.field}</span>
      {f.current != null && f.expected != null ? (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8, ...mono, fontSize: 14 }}><span style={{ color: "var(--text-subtle)", textDecoration: "line-through" }}>{f.current}</span><Icon name="arrow-right" size={14} /><span style={{ color: "var(--text-strong)", fontWeight: 600 }}>{f.expected}</span></span>
      ) : f.expected ? (
        <span style={{ ...mono, fontSize: 14, color: "var(--text-strong)", whiteSpace: "normal" }}>{f.expected}</span>
      ) : f.current ? (
        <span style={{ ...mono, fontSize: 14, color: "var(--text-strong)" }}>{f.current}</span>
      ) : null}
      {hand && <span style={{ font: "600 13px var(--font-body)", color: "var(--text-muted)" }}>then press Check again</span>}
      <span style={{ flex: 1 }} />
      {onFix && detail.invoice.asInvoiced && session.canAct && <Button size="sm" leadingIcon={<Icon name="check" size={14} />} onClick={onFix}>Approve as invoiced</Button>}
      {(syncoreUrl ?? detail.po?.syncoreUrl) && <Button size="sm" variant="outline" trailingIcon={<Icon name="external-link" size={14} />} onClick={() => window.open(syncoreUrl ?? detail.po!.syncoreUrl, "_blank", "noopener")}>{inv.poNumber ? "Open PO" : "Open Syncore"}</Button>}
    </div>
  );
}

export function FixInline({ detail }: { detail: InvoiceDetail | null }) {
  if (!detail) return <span style={{ color: "var(--text-subtle)" }}>—</span>;
  const f = fixOf(detail);
  if (!f) return <span style={{ color: "var(--text-subtle)" }}>—</span>;
  if (f.kind === "Manual") return <span style={{ font: "600 13px var(--font-body)" }}><span style={{ color: "var(--text-muted)" }}>Fix in Syncore – </span>{f.field}</span>;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap", font: "13px var(--font-body)" }}>
      <span style={{ color: "var(--text-muted)", fontWeight: 600 }}>{f.field}</span>
      {f.current != null && f.expected != null ? <span style={{ display: "inline-flex", alignItems: "center", gap: 5, ...mono }}><span style={{ color: "var(--text-subtle)", textDecoration: "line-through" }}>{f.current}</span><Icon name="arrow-right" size={12} /><b style={{ color: "var(--text-strong)", fontWeight: 600 }}>{f.expected}</b></span> : <span style={{ ...mono, whiteSpace: "normal" }}>{f.expected ?? f.current ?? ""}</span>}
    </span>
  );
}

/** The browser's page zoom, as a value that changes whenever the person zooms the page (Ctrl +/−). Zooming inside the
 *  PDF itself is the viewer's own and leaves it alone. */
function usePageZoom(): number {
  const [zoom, setZoom] = useState(() => window.devicePixelRatio);
  useEffect(() => {
    let mq: MediaQueryList | null = null;
    const onChange = () => { setZoom(window.devicePixelRatio); watch(); };
    const watch = () => {
      mq?.removeEventListener("change", onChange);
      mq = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
      mq.addEventListener("change", onChange);
    };
    watch();
    return () => mq?.removeEventListener("change", onChange);
  }, []);
  return zoom;
}

/** The invoice PDF, fetched with the bearer token and shown inline. */
export function PdfView({ invoiceId, fileName, available, height }: { invoiceId: string; fileName: string; available: boolean; height?: number | string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The viewer fits the page to the frame's width when it loads; browser zoom would then grow the PDF past the frame,
  // so the frame is remounted at every page-zoom change and fits again.
  const zoom = usePageZoom();
  useEffect(() => {
    let object: string | null = null;
    let cancelled = false;
    setUrl(null);
    setError(null);
    if (!available) { setError("PDF no longer kept – deleted under your retention policy."); return; }
    apiBlob(`/invoices/${invoiceId}/pdf`).then(({ blob }) => {
      if (cancelled) return;
      object = URL.createObjectURL(blob);
      setUrl(object);
    }).catch((e: unknown) => {
      if (cancelled) return;
      setError(e instanceof ApiError && e.status === 410 ? "PDF no longer kept – deleted under your retention policy." : e instanceof ApiError && e.status === 404 ? "The PDF is no longer stored." : e instanceof Error ? e.message : String(e));
    });
    return () => { cancelled = true; if (object) URL.revokeObjectURL(object); };
  }, [invoiceId, available]);
  return (
    <div style={{ height: height || "100%", display: "flex", flexDirection: "column", background: "var(--av-gray-100)", borderRadius: "var(--radius-lg)", overflow: "hidden", boxShadow: "inset 0 0 0 1px var(--border-subtle)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "8px 14px", background: "#fff", borderBottom: "1px solid var(--border-subtle)", font: "600 12px var(--font-body)", color: "var(--text-muted)" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{fileName}</span><span style={{ flex: 1 }} />
        {url && <a href={url} download={fileName} style={{ display: "inline-flex", color: "var(--text-brand)" }} aria-label="Download PDF"><Icon name="download" size={15} /></a>}
      </div>
      <div style={{ flex: 1, minHeight: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {error ? <span style={{ font: "600 13px var(--font-body)", color: "var(--text-muted)", padding: 20, textAlign: "center" }}>{error}</span> : url ? <iframe key={zoom} title={fileName} src={url + "#toolbar=0&view=FitH"} style={{ width: "100%", height: "100%", border: 0, background: "#fff" }} /> : <Spinner size={22} />}
      </div>
    </div>
  );
}

function LineTable({ title, lines, totals, other, currency }: { title: ReactNode; lines: Line[] | null; totals: Totals | null; other: Totals | null; currency: string }) {
  const hl = { background: "var(--status-review-bg)", color: "var(--status-review-fg)" };
  const cell = { padding: "7px 8px", borderTop: "1px solid var(--border-subtle)", font: "13px var(--font-body)" };
  const num = { ...cell, ...mono, textAlign: "right" as const };
  if (!lines) return <div><div style={{ font: "800 14px var(--font-display)", color: "var(--text-strong)", marginBottom: 6 }}>{title}</div><div style={{ font: "var(--type-body-sm)", color: "var(--text-subtle)", padding: "12px 0" }}>Not read</div></div>;
  const sum = (l: string, a: number | null | undefined, b: number | null | undefined) => {
    const d = other && a != null && b != null && Math.abs(a - b) > 0.001;
    return <tr key={l} style={d ? hl : undefined}><td style={{ ...cell, fontWeight: 700 }} colSpan={3}>{l}</td><td style={{ ...num, fontWeight: l === "Total" ? 700 : 500 }}>{a == null ? "—" : amount(a)}</td></tr>;
  };
  void currency;
  return (
    <div>
      <div style={{ font: "800 14px var(--font-display)", color: "var(--text-strong)", marginBottom: 6 }}>{title}</div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Item", "Qty", "Unit", "Amount"].map((h, i) => <th key={h} style={{ ...th, padding: "7px 8px", textAlign: i ? "right" : "left" }}>{h}</th>)}</tr></thead>
          <tbody>
            {lines.map((l, i) => <tr key={i}><td style={cell}>{l.description ?? l.code ?? ""}{l.code && l.description ? <span style={{ color: "var(--text-subtle)" }}> – {l.code}</span> : null}{l.kind && l.kind !== "Product" && l.kind !== "Line" ? <span style={{ color: "var(--text-subtle)" }}> ({l.kind})</span> : null}</td><td style={num}>{l.quantity ?? "—"}</td><td style={num}>{l.unitPrice == null ? "—" : amount(l.unitPrice)}</td><td style={num}>{l.amount == null ? "—" : amount(l.amount)}</td></tr>)}
            {lines.length === 0 && <tr><td colSpan={4} style={{ ...cell, color: "var(--text-subtle)" }}>No lines</td></tr>}
            {sum("Freight", totals?.freight, other?.freight)}
            {sum("Tax", totals?.tax, other?.tax)}
            {sum("Total", totals?.total, other?.total)}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function CompareTables({ detail, currency }: { detail: InvoiceDetail; currency: string }) {
  const inv = detail.invoice;
  return (
    <div className="avp-cmp" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 24 }}>
      {detail.poLines ? <LineTable title={<SourceTitle kind="syncore">PO {inv.jobNumber}-{inv.poNumber}</SourceTitle>} lines={detail.poLines} totals={detail.poTotals} other={detail.invoiceTotals} currency={currency} /> : <div><div style={{ marginBottom: 6 }}><SourceTitle kind="syncore">PO</SourceTitle></div><div style={{ font: "var(--type-body-sm)", color: "var(--text-subtle)", padding: "12px 0" }}>{detail.poError ? plainSyncoreError(detail.poError) : "No PO found"}</div></div>}
      <LineTable title={<SourceTitle kind="pdf">Invoice {inv.invoiceNumber ?? ""}</SourceTitle>} lines={detail.invoiceLines} totals={detail.invoiceTotals} other={detail.poTotals} currency={currency} />
    </div>
  );
}

/** Which side a column comes from: the Syncore logo over the PO, a PDF mark over the invoice. */
function SourceTitle({ kind, children }: { kind: "syncore" | "pdf"; children: ReactNode }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10, font: "800 14px var(--font-display)", color: "var(--text-strong)" }}>
      {kind === "syncore"
        ? <img src="/syncore-logo.svg" alt="Syncore" style={{ height: 14, width: "auto", display: "block" }} />
        : <span aria-label="PDF" style={{ display: "inline-flex", alignItems: "center", gap: 4, height: 18, padding: "0 6px 0 4px", borderRadius: 5, background: "var(--av-coral-500)", color: "#fff", font: "800 10px/1 var(--font-body)", letterSpacing: ".04em" }}><Icon name="file-text" size={11} stroke={2.5} />PDF</span>}
      <span>{children}</span>
    </span>
  );
}

export function DiffTable({ detail }: { detail: InvoiceDetail }) {
  const cell = { padding: "8px 10px", borderTop: "1px solid var(--border-subtle)", font: "13px var(--font-body)" };
  const num = { ...cell, ...mono, textAlign: "right" as const };
  if (!detail.poLines) return <div style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>{detail.poError ? plainSyncoreError(detail.poError) : "No PO to compare against."}</div>;
  const rows: [string, string, string, string][] = [];
  const t = detail.poTotals, u = detail.invoiceTotals;
  const pair = (label: string, a: number | null | undefined, b: number | null | undefined) => {
    if (a == null || b == null || Math.abs(a - b) <= 0.001) return;
    rows.push([label, amount(a), amount(b), signed(b - a)]);
  };
  for (const d of detail.discrepancies ?? []) {
    if (d.code.startsWith("LINE")) rows.push([d.message, "", "", d.delta != null ? signed(d.delta) : ""]);
  }
  pair("Freight", t?.freight, u?.freight);
  pair("Tax", t?.tax, u?.tax);
  pair("Total", t?.total, u?.total);
  if (!rows.length) return <div style={{ display: "flex", alignItems: "center", gap: 6, font: "600 13px var(--font-body)", color: "var(--status-matched-fg)" }}><CheckIcon r="ok" size={16} />Every line, freight, tax and the total match</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead><tr>{["", "PO", "Invoice", "Difference"].map((h, i) => <th key={i} style={{ ...th, padding: "7px 10px", textAlign: i ? "right" : "left" }}>{h}</th>)}</tr></thead>
        <tbody>{rows.map((d, i) => <tr key={i}><td style={{ ...cell, fontWeight: 700 }}>{d[0]}</td><td style={num}>{d[1]}</td><td style={num}>{d[2]}</td><td style={{ ...num, color: "var(--status-mismatch-fg)", fontWeight: 600 }}>{d[3]}</td></tr>)}</tbody>
      </table>
    </div>
  );
}

export function AuditList({ entries, showInvoice }: { entries: AuditEntry[]; showInvoice?: boolean }) {
  const cell = { padding: "8px 10px", font: "13px/1.4 var(--font-body)", verticalAlign: "top" as const };
  const sorted = entries.slice().sort((a, b) => b.at.localeCompare(a.at));
  if (!sorted.length) return <div style={{ font: "var(--type-body-sm)", color: "var(--text-muted)" }}>Nothing yet.</div>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <tbody>
          {sorted.map((e, i) => (
            <tr key={e.id} style={i ? { borderTop: "1px solid var(--border-subtle)" } : undefined}>
              <td style={{ ...cell, color: "var(--text-muted)", whiteSpace: "nowrap" }}>{fmtWhen(e.at)}</td>
              <td style={{ ...cell, whiteSpace: "nowrap" }}>{actorName(e.actor)}</td>
              <td style={{ ...cell, fontWeight: 700, color: "var(--text-strong)", whiteSpace: "nowrap" }}>{eventWords(e.event)}{e.dryRun ? <span style={{ fontWeight: 500, color: "var(--text-subtle)" }}> (preview)</span> : null}</td>
              <td style={{ ...cell, color: "var(--text-muted)" }}>{auditDetail(e)}</td>
              {showInvoice && <td style={{ ...cell, ...mono, fontWeight: 500 }}>{e.jobNumber && e.poNumber ? `PO ${e.jobNumber}-${e.poNumber}` : e.invoiceId ? "Invoice" : ""}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function auditDetail(e: AuditEntry): ReactNode {
  const { before, after, detail } = e.detail;
  if (before && after && before !== after) return <span style={{ display: "inline-flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}><span style={{ color: "var(--text-subtle)", textDecoration: "line-through" }}>{before}</span><Icon name="arrow-right" size={12} /><b style={{ color: "var(--text-strong)" }}>{after}</b>{detail ? <span> – {detail}</span> : null}</span>;
  return after && detail ? `${after} – ${detail}` : after ?? detail ?? "";
}

const STEP_WORDS: Record<string, string> = { Received: "Received", Extracted: "Read", Vouched: "Approved", NeedsReview: "Needs review", Failed: "Can’t match", Held: "Held", FreightUpdated: "PO freight updated", AdjustmentLineAdded: "Adjustment line added", InvoiceDetailsRecorded: "Invoice date recorded", LimitWaived: "Approved over the limit", AttachmentUploaded: "PDF attached", AttachmentReused: "PDF already attached", PurchaseOrderApproved: "PO approved", SpendCapReached: "Reading budget reached" };

function timelineStep(step: string): string {
  return STEP_WORDS[step] ?? eventWords(step);
}

/** "FREIGHT: Freight 64.68 vs PO 36.85" → the words after the code. This is the technical tab, so a technical stop keeps what Syncore or the worker actually said. */
function timelineDetail(text: string | null): string {
  if (!text) return "";
  const m = /^([A-Z][A-Z0-9_*]+): (.*)$/.exec(text);
  return m ? m[2] : text;
}

export function TechDetails({ detail }: { detail: InvoiceDetail }) {
  const x = detail.extraction;
  const read = detail.timeline.find((t) => t.step === "Extracted");
  const rows: [string, ReactNode][] = [
    ["Read by", x?.model ?? "—"],
    ["Read a second time", x?.escalated ? "Yes" : "No"],
    ["Time to read", read?.durationMs ? (read.durationMs / 1000).toFixed(1) + " s" : "—"],
    ["Source", `${detail.invoice.source} – ${detail.invoice.fileName}`],
    ["Attempts", String(detail.invoice.attempts)],
    ["Outcome code", detail.invoice.code ?? "—"],
  ];
  if (detail.attachment) rows.push(["Attachment", detail.attachment.reused ? "Already on the job" : detail.attachment.id ? `Attached (${detail.attachment.id})` : "Attached"]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <KV rows={rows} columns="160px 1fr" />
      {x?.warnings?.length ? <div style={{ font: "600 13px var(--font-body)", color: "var(--status-review-fg)" }}>{x.warnings.join(" – ")}</div> : null}
      <div style={{ font: "700 12px var(--font-body)", color: "var(--text-muted)" }}>Timeline</div>
      <table style={{ width: "100%", borderCollapse: "collapse" }}><tbody>
        {detail.timeline.map((t, i) => <tr key={i}><td style={{ ...td, padding: "6px 8px", fontWeight: 700, color: t.ok ? "var(--text-strong)" : "var(--status-mismatch-fg)" }}>{timelineStep(t.step)}</td><td style={{ ...td, padding: "6px 8px", color: "var(--text-muted)", whiteSpace: "nowrap" }}>{t.at ? fmtDateTime(t.at) : ""}</td><td style={{ ...td, padding: "6px 8px", color: "var(--text-muted)" }}>{timelineDetail(t.detail)}</td></tr>)}
      </tbody></table>
      {x?.json != null && <pre style={{ margin: 0, padding: "12px 14px", borderRadius: "var(--radius-md)", background: "var(--surface-sunken)", font: "12px/1.5 var(--font-mono)", color: "var(--text-body)", overflow: "auto", maxHeight: 360 }}>{JSON.stringify(x.json, null, 2)}</pre>}
    </div>
  );
}

/** An icon-only link to the invoice's job in Syncore (the API builds PO links the same way: Job/Details/{job}). */
/** Every check passed; a freight or lines adjustment AutoVouch made (or will make) by rule shows beside the count. */
export function ChecksPassed({ freight, lines }: { freight?: boolean; lines?: boolean }) {
  const adjusted = (freight ? 1 : 0) + (lines ? 1 : 0);
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
      <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--status-matched-fg)", font: "800 13px var(--font-body)" }}><CheckIcon r="ok" size={16} />{13 - adjusted}</span>
      {freight && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--status-review-fg)", font: "600 13px var(--font-body)" }}><CheckIcon r="adj" size={16} />Freight adjusted</span>}
      {lines && <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: "var(--status-review-fg)", font: "600 13px var(--font-body)" }}><CheckIcon r="adj" size={16} />Lines adjusted</span>}
    </span>
  );
}

export function JobLink({ jobNumber, size = 16 }: { jobNumber: number | null | undefined; size?: number }) {
  const syncore = useSyncore();
  const base = syncore.data?.webBaseUrl;
  if (!jobNumber || !base) return null;
  const href = base.replace(/\/?$/, "/") + "Job/Details/" + jobNumber;
  return (
    <Hint text="Open Job in Syncore" width={160} below>
      <a href={href} target="_blank" rel="noopener" aria-label="Open Job in Syncore" onClick={(e) => e.stopPropagation()} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: size + 16, height: size + 16, borderRadius: 99, color: "var(--text-link)" }}><Icon name="external-link" size={size} /></a>
    </Hint>
  );
}

export function InvoiceHeading({ detail, currency, children }: { detail: InvoiceDetail; currency: string; children?: ReactNode }) {
  const inv = detail.invoice;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
      <h2 style={{ font: "800 26px/1.15 var(--font-display)", letterSpacing: "-0.01em", margin: 0 }}>{inv.supplier ?? invoiceRef(inv)}</h2>
      {inv.invoiceNumber && <span style={{ ...mono, fontSize: 16, color: "var(--text-muted)" }}>{inv.invoiceNumber}</span>}
      <span style={{ ...mono, fontSize: 16, color: "var(--text-strong)" }}>{money(inv.total, currency)}</span>
      <JobLink jobNumber={inv.jobNumber} />
      {children}
    </div>
  );
}

export { table };
export { STEPS };
